"use client"
import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react"
import { useNavigate } from 'react-router-dom'
import { message } from "antd"
import { apiRequest } from '../utils/apiRequest'
import { useAuth } from "./AuthContext"
import { useCart } from "./CartContext"
import { useWallet} from "./WalletContext"

const OrderContext = createContext(undefined)

export const OrderProvider = ({ children }) => {
  const [loading, setLoading] = useState(false)
  const [orders, setOrders] = useState([])
  const [sortOrder, setSortOrder] = useState("default")
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(5)
  const [total,setTotal] = useState(0)
  const [searchPhrase, setSearchPhrase] = useState('')
  const [paymentMethods, setPaymentMethods] = useState([])
  const [savedCards, setSavedCards] = useState([])
  const [selectedCardToken, setSelectedCardToken] = useState(null)
  const [transaction, setTransaction] = useState({})
  const [peachOrderInProgress, setPeachOrderInProgress] = useState(null) // Track Peach orders
  const lastPaymentMethodsFetch = useRef(null)

  const { balance , getWallet } = useWallet()
  const navigate = useNavigate()

  useEffect(()=> {
    getPaymentMethods()
  },[])

  const placeOrder = async (data, modalCallbacks = null) => {
    try {
      setLoading(true)
      const response = await apiRequest({
        method: "POST",
        url: "/order",
        data
      })

      if (response.status === 200) {
        await setTransaction(response.data.transaction)
        await getWallet()
        //clearCart()

        //navigate("/account")
        return response.data;
      }

      if(response.status === 201){
         setTransaction(response.data.transaction)
         //clearCart()
         //return response.data;

         // Handle different payment methods - they have different response formats
         const paymentMethod = response.data.transaction?.metadata?.payment_method || 'unknown';

         let redirectUrl;

         // Handle Stripe: return clientSecret for frontend PaymentElement
         if (paymentMethod === 'Stripe') {
           const clientSecret = response.data.transaction.metadata.checkout?.clientSecret;
           if (modalCallbacks?.onStripePayment) {
             modalCallbacks.onStripePayment(clientSecret, response.data.transaction.order_id);
           }
           return response.data;
         }

         if (paymentMethod === 'Peach') {
           // Peach Hosted Checkout URL is in checkout.redirectUrl
           redirectUrl = response.data.transaction.metadata.checkout?.redirectUrl;
         } else if (paymentMethod === 'Yoco') {
           // Yoco returns redirectUrl in the checkout object
           redirectUrl = response.data.transaction.metadata.checkout.redirectUrl;
         } else {
           // Fallback - try multiple possible locations
           redirectUrl = response.data.transaction.metadata.checkout.redirectUrl ||
                        response.data.transaction.metadata.checkout.paymentLinkUrl ||
                        response.data.transaction.metadata.checkout.rawResponse?.url;
         }

         if (redirectUrl) {
           if (paymentMethod === 'Peach') {
             // Peach: Open in new tab and handle success via webhooks
             console.log(`Opening Peach payment URL in new tab:`, redirectUrl);
             message.info('Complete your payment in the new tab. Your order will be processed automatically.', 5);

             // Store order tracking for success detection
             setPeachOrderInProgress({
               orderId: response.data.transaction.order_id,
               callbacks: modalCallbacks
             });

             window.open(redirectUrl, '_blank');

             // Store transaction for success detection
             setTransaction(response.data.transaction);
           } else {
             // Yoco and others: Redirect in current tab (existing behavior)
             console.log(`Redirecting to ${paymentMethod} payment URL:`, redirectUrl);
             window.location.href = redirectUrl;
           }
         } else {
           console.error('No redirect URL found for payment method:', paymentMethod, response.data.transaction.metadata);
         }
      }
    } catch (error) {
      console.error("Failed to place order:", error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const checkOrderStatus = async (order, paymentmethod, status) => {
    try {
      setLoading(true)
     const response = await apiRequest({
        method: "get",
        url: `checkorder?order=${order}&paymentmethod=${paymentmethod}&status=${status}`,
      });

    } catch (error) {
      console.error(error);
      return { success: false, error };
    } finally {
      setLoading(false)
    }

  }

  const getOrders = useCallback(async (s, pageSize=5, currentPage=1) => {
    try {
      setLoading(true)
      let url = `/orders`;
      const params = new URLSearchParams();
      if (pageSize && !isNaN(pageSize) && pageSize > 0) {params.append('limit', pageSize)}
      if (currentPage) { params.append('page', currentPage);}
      if (s) { params.append('search', s);}
      params.append('sortorder', 'desc');
      url += `?${params.toString()}`;

      const response = await apiRequest({
        method: "get",
        url,
      });
      
      if (response.data) {
        setOrders(response.data.data);
        response.data.pagination.total && setTotal(response.data.pagination.total )
        setPageSize(response.data.pagination.perPage)
      }
      return response;
    } catch (error) {
      console.error(error);
      return { success: false, error };
    } finally {
      setLoading(false)
    }
  }, []);

  const getPaymentMethods = useCallback(async (force = false) => {
    // Skip if data is fresh (unless forced)
    const STALE_TIME = 10 * 60 * 1000; // 10 minutes
    if (!force && lastPaymentMethodsFetch.current &&
        Date.now() - lastPaymentMethodsFetch.current < STALE_TIME &&
        paymentMethods.length > 0) {
      return;
    }

    try {
      let url = `/payment_methods?isActive=1`;

      const response = await apiRequest({
        method: "get",
        url,
        suppressNotifications: true,
      });

      if (response.data) {
        setPaymentMethods(response.data)
        lastPaymentMethodsFetch.current = Date.now();
      }
      return response;
    } catch (error) {
      console.error(error);
      return { success: false, error };
    }
  }, [paymentMethods.length]);

  const getSavedCards = useCallback(async () => {
    try {
      const response = await apiRequest({
        method: "get",
        url: "/payment-tokens",
      });

      if (response.data?.tokens) {
        setSavedCards(response.data.tokens);

        // Auto-select default card if available
        const defaultCard = response.data.tokens.find(card => card.is_default);
        if (defaultCard && !selectedCardToken) {
          setSelectedCardToken(defaultCard.id);
        }
      }
      return response;
    } catch (error) {
      console.error("Failed to fetch saved cards:", error);
      return { success: false, error };
    }
  }, [selectedCardToken]);

  const setDefaultCard = async (cardTokenId) => {
    try {
      const response = await apiRequest({
        method: "PATCH",
        url: `/payment-tokens/${cardTokenId}/default`,
      });

      if (response.data) {
        // Refresh saved cards to get updated default status
        await getSavedCards();
        message.success('Default payment method updated');
      }
      return response;
    } catch (error) {
      console.error("Failed to set default card:", error);
      message.error('Failed to update default payment method');
      return { success: false, error };
    }
  };

  const deleteSavedCard = async (cardTokenId) => {
    try {
      const response = await apiRequest({
        method: "DELETE",
        url: `/payment-tokens/${cardTokenId}`,
      });

      if (response.status === 200) {
        // Refresh saved cards
        await getSavedCards();
        message.success('Payment method deleted successfully');

        // Clear selected card if it was deleted
        if (selectedCardToken === cardTokenId) {
          setSelectedCardToken(null);
        }
      }
      return response;
    } catch (error) {
      console.error("Failed to delete saved card:", error);
      message.error('Failed to delete payment method');
      return { success: false, error };
    }
  };

  // SUCCESS DETECTION FOR PEACH PAYMENTS (to match Yoco UX)
  useEffect(() => {
    if (!transaction?.metadata?.payment_method?.includes('Peach')) return;

    const checkPeachSuccess = async () => {
      try {
        // Check if the Peach order has been completed via webhook
        const ordersResponse = await getOrders();
        if (!ordersResponse?.data?.data) return;

        const peachOrder = ordersResponse.data.data.find(order =>
          order.payment_method === 'Peach' &&
          order.status === 'delivered' &&
          order.id === transaction.order_id // Match the transaction we stored
        );

        if (peachOrder) {

          // Clear cart (localStorage) - same as Yoco
          localStorage.removeItem('cart');

          // Call modal callbacks if available (for checkout page)
          if (peachOrderInProgress?.callbacks) {
            // Close loading and show order complete
            peachOrderInProgress.callbacks.onOrderUpdate?.(peachOrder);
            peachOrderInProgress.callbacks.onOrderComplete?.();
          } else {
            // Fallback to navigation flow (existing behavior)
            // Show success message
            message.success('Payment successful! Order completed.');

            // Navigate to orders page (same as Yoco)
            navigate('/account/orders');
          }

          // Refresh wallet and orders
          await getWallet();
          await getOrders();

          // Clear tracking
          setTransaction({});
          setPeachOrderInProgress(null);
        }
      } catch (error) {
        console.error('Error checking Peach payment status:', error);
      }
    };

    // Check every 3 seconds for Peach payment completion
    const interval = setInterval(checkPeachSuccess, 3000);

    // Cleanup interval when transaction changes or component unmounts
    return () => clearInterval(interval);
  }, [transaction, getOrders, getWallet, navigate]);

  return (
    <OrderContext.Provider
      value={{
        orders,
        placeOrder,
        getPaymentMethods,
        checkOrderStatus,
        paymentMethods,
        savedCards,
        selectedCardToken,
        setSelectedCardToken,
        getSavedCards,
        setDefaultCard,
        deleteSavedCard,
        transaction,
        getOrders,
        loading,
        searchPhrase,
        setSearchPhrase,
        pageSize,
        setPageSize,
        currentPage,
        setCurrentPage,
        total,
        setTotal
      }}
    >
      {children}
    </OrderContext.Provider>
  )
}

export const useOrders = () => {
  const context = useContext(OrderContext)
  if (context === undefined) {
    throw new Error("useOrders must be used within a OrderProvider")
  }
  return context
}
